import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useExportDownload } from "@/hooks/useExportDownload";
import { formatDate, formatTime, formatHours, formatCZK, plur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Download, Users, Briefcase, CalendarDays, CalendarRange, Calendar, FileSpreadsheet, Clock, Wallet, ListChecks, Pencil, ChevronDown, Camera, Table2, FileText, Flag, Banknote } from "lucide-react";
import { toast } from "sonner";
import { StatCard, useIsMobile, useIsNarrow } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useStoredView, viewEff, groupSortProp } from "@/components/shared/GroupSort";
import { storage } from "@/lib/storage";
import { useListFilters } from "@/hooks/useListFilters";
import { ShiftReviewDialog } from "@/components/shared/ShiftReviewDialog";
import { AttendanceSingleDialog, AttendanceBulkDialog } from "@/components/AttendanceAddDialogs";
import AbsenceSheet from "@/components/absences/AbsenceSheet";
import WorkerAbsence from "@/components/absences/WorkerAbsence";
import { AbsenceDialog } from "@/components/absences/AbsenceDialog";
import { groupKey, groupLabel, monthStart, monthEnd, todayIso, monthLabel } from "@/lib/absences";
import { sheetMenuItems, downloadSheet, PAY_ROLES } from "@/lib/sheetExport";
import { useAuth } from "@/context/AuthContext";
import { diffDays, toDate } from "@/lib/days";
import { useDebouncedValue, useLatestOnly } from "@/hooks/useDebouncedValue";
import { useListNav, Kbd } from "@/components/shared/ListNav";

// 346: jednotná lišta — rychlé záložky = období, Filtr (Člověk / Zakázka / Období / Zobrazit jen) filtruje
// na serveru, pohledy Záznamy / Šichtovnice (Můj kalendář) vpravo.
// 379: Řadit → Zobrazení (V) jako všude: Seskupit podle Den / Týden / Měsíc / Pracovník / Zakázka (dřívější Podle lidí
// a Podle zakázek = skupiny se součty v liště, u člověka Výkaz →), Řadit podle uvnitř. Bez skupin a s výchozím
// řazením (nejnovější) stránkuje server po 50; jinak se načte až 2 000 záznamů a skládá se tady.
const BY_HOURS = ["Nejvíc hodin", "A → Z"];
const ATT_GROUPS = {
  day: { label: "Den", icon: CalendarDays, dirs: ["Nejnovější", "Nejstarší"] },
  week: { label: "Týden", icon: CalendarRange, dirs: ["Nejnovější", "Nejstarší"] },
  month: { label: "Měsíc", icon: Calendar, dirs: ["Nejnovější", "Nejstarší"] },
  worker: { label: "Pracovník", icon: Users, dirs: BY_HOURS },
  project: { label: "Zakázka", icon: Briefcase, dirs: BY_HOURS },
};
const ATT_ORDERS = {
  date: { label: "Datum", icon: CalendarDays, dirs: ["Nejstarší", "Nejnovější"], def: "desc" },
  worker: { label: "Pracovník", icon: Users, dirs: ["A → Z", "Z → A"], def: "asc" },
  project: { label: "Zakázka", icon: Briefcase, dirs: ["A → Z", "Z → A"], def: "asc" },
  hours: { label: "Hodiny", icon: Clock, dirs: ["Od nejmenších", "Od největších"], def: "desc" },
};
const ATT_VIEW = { g: "none", gd: 0, k: "date", dir: "desc" };
const TG_KEY = "proyekty.attendance.timeGroup";   // dřívější Seskupit záznamy (176)
const cs = (a, b) => (a || "").localeCompare(b || "", "cs");
const ORDER_CMP = {
  date: (a, b) => cs(`${a.date} ${a.check_in}`, `${b.date} ${b.check_in}`),
  worker: (a, b) => cs(a.user_name, b.user_name), project: (a, b) => cs(a.project_name, b.project_name),
  hours: (a, b) => (a.hours || 0) - (b.hours || 0),
};
const recKey = (r, by) => (by === "worker" ? r.user_id || r.user_name : by === "project" ? r.project_id || "" : groupKey(r.date, by));
const PERIODS = [{ value: "today", label: "Dnes" }, { value: "week", label: "Týden" }, { value: "month", label: "Měsíc" }, { value: "all", label: "Vše" }];
const FLAGS = [{ value: "no_radius", label: "Mimo radius" }, { value: "no_report", label: "Dny bez reportu" },
  { value: "no_gps", label: "Ručně zadané" }, { value: "pending", label: "Ke schválení" }];
const czDate = (x) => (x ? new Date(x).toLocaleDateString("cs-CZ") : "");
const rangeText = (raw) => { const [a, b] = String(raw || "").split(".."); return `${czDate(a) || "…"} – ${czDate(b) || "…"}`; };

const todayStr = () => new Date().toISOString().slice(0, 10);
function rangeFor(kind) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (kind === "today") return [iso(now), iso(now)];
  if (kind === "week") {
    const d = new Date(now); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd);
    return [iso(d), iso(now)];
  }
  if (kind === "month") return [now.toISOString().slice(0, 7) + "-01", iso(now)];
  return ["", ""];
}

function RunningBadge() {
  return <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-brand"><span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />probíhá</span>;
}

export default function AttendanceOverview({ topSlot }) {
  const nav = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("month");
  const [add, setAdd] = useState(null);        // null | "single" | "bulk"
  const [detail, setDetail] = useState(null);  // { r, edit } — jeden dialog pro čtení i opravu
  // 219: pole hledání píše hned, do filtru (a tím do requestu) jde až po pauze v psaní.
  const [qInput, setQInput] = useState("");
  const qDebounced = useDebouncedValue(qInput, 350);
  const latest = useLatestOnly();
  const ctrl = useRef(null);
  const [params, setParams] = useSearchParams();
  const TAB_PARAM = { sheet: "sichtovnice", calendar: "kalendar" };
  const view = params.get("tab") === "sichtovnice" ? "sheet" : params.get("tab") === "kalendar" ? "calendar" : "records";
  // Jako Kanban u úkolů (176d): Šichtovnice jen na šířku (≥ 1024 px);
  // mobil a tablet na výšku mají místo ní vlastní kalendář.
  const narrow = useIsNarrow();
  const effView = view === "sheet" && narrow ? "records" : view === "calendar" && !narrow ? "records" : view;
  const setView = (v) => { const p = new URLSearchParams(params); if (TAB_PARAM[v]) p.set("tab", TAB_PARAM[v]); else p.delete("tab"); setParams(p, { replace: true }); };
  const [gsRaw, setGs] = useStoredView("attendance_order", ATT_GROUPS[storage.get(TG_KEY)] ? { ...ATT_VIEW, g: storage.get(TG_KEY) } : ATT_VIEW);
  const gs = viewEff(gsRaw, ATT_GROUPS, ATT_ORDERS, ATT_VIEW);
  const needAll = gs.g !== "none" || gs.k !== ATT_VIEW.k || gs.dir !== ATT_VIEW.dir;
  const [absOpen, setAbsOpen] = useState(false);
  const [sheetKey, setSheetKey] = useState(0);
  const [sheetState, setSheetState] = useState(null);
  const [pendingAbs, setPendingAbs] = useState(0);
  useEffect(() => {
    api.get("/absences/pending").then((r) => setPendingAbs((r.data.items || []).length)).catch(() => {});
  }, [sheetKey, view]);

  useEffect(() => {
    api.get("/team").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/projects/options").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const fGroups = useMemo(() => [
    { key: "clovek", label: "Člověk", icon: Users, noNeg: true, options: workers.map((w) => ({ value: w.id, label: w.name })) },
    { key: "zakazka", label: "Zakázka", icon: Briefcase, noNeg: true, options: projects.map((p) => ({ value: p.id, label: p.name })) },
    { key: "obdobi", label: "Období", icon: CalendarDays, type: "range", format: rangeText },
    { key: "jen", label: "Zobrazit jen", icon: Flag, noNeg: true, options: FLAGS },
  ], [workers, projects]);
  const F = useListFilters(null, { groups: fGroups, entity: { one: "záznam", few: "záznamy", many: "záznamů" } });
  // 236: odkaz z úvodu „Otevřít v Docházce“ (?ke-schvaleni=1) zapne rovnou filtr Ke schválení
  useEffect(() => {
    if (params.get("ke-schvaleni") !== "1") return;
    const p = new URLSearchParams(params); p.delete("ke-schvaleni"); p.set("jen", "pending");
    setParams(p, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fKey = JSON.stringify(F.state);
  const qs = useMemo(() => {
    const st = F.state, p = new URLSearchParams();
    const [df, dt] = st.obdobi ? st.obdobi.vals[0].split("..") : rangeFor(period);
    if (df) p.set("date_from", df);
    if (dt) p.set("date_to", dt);
    if (st.clovek) p.set("worker_ids", st.clovek.vals.join(","));
    if (st.zakazka) p.set("project_ids", st.zakazka.vals.join(","));
    if (qDebounced.trim()) p.set("q", qDebounced.trim());
    (st.jen?.vals || []).filter((k) => FLAGS.some((x) => x.value === k)).forEach((k) => p.set(k, "true"));
    return p.toString();
  }, [fKey, period, qDebounced]); // eslint-disable-line react-hooks/exhaustive-deps
  const buildQs = useCallback((extra = {}) => {
    const p = new URLSearchParams(qs);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [qs]);

  // 219: platí jen odpověď na poslední request (starší se zruší a případně zahodí)
  // a při obnově zůstávají stará data vidět (ztlumená), žádné „Načítám…“ místo seznamu.
  const load = useCallback(async () => {
    const isLatest = latest();
    ctrl.current?.abort();
    const c = typeof AbortController !== "undefined" ? new AbortController() : null;
    ctrl.current = c;
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance/overview?${buildQs({ group: "day", page: needAll ? 1 : page, page_size: needAll ? 2000 : 50 })}`,
        c ? { signal: c.signal } : undefined);
      if (isLatest()) setData(data);
    } catch (e) {
      if (isLatest() && e?.code !== "ERR_CANCELED") toast.error(formatApiError(e.response?.data?.detail));
    } finally { if (isLatest()) setLoading(false); }
  }, [buildQs, page, needAll, latest]);

  useEffect(() => { if (effView === "records") load(); }, [load, effView]);
  useEffect(() => () => ctrl.current?.abort(), []);
  useEffect(() => { setPage(1); }, [qs]);
  // Rychlá záložka = období; vlastní rozsah z filtru Období má přednost (záložka pak nesvítí).
  const pickPeriod = (k) => { setPeriod(k); F.clearOne("obdobi"); };
  const mobile = useIsMobile();

  // 217: export běží jako stažení blobu — „Stáhnout" je mezitím zakázané a běží hláška.
  const { busy: exporting, run: runExport } = useExportDownload();
  const doExport = (fmt) => runExport(fmt, `/api/attendance/overview/export?${buildQs({ fmt })}`, `dochazka_prehled.${fmt}`, "Připravuji export docházky…");
  // Šichtovnice za to, co je zrovna vybrané (402): v Šichtovnici její měsíc a lidé, v Záznamech Období z filtru
  // (do dvou měsíců, jinak jeho poslední měsíc) a Člověk, jinak aktuální měsíc. Dřív Záznamy s Obdobím říjen
  // (a telefon, kde Šichtovnice není vůbec) stáhly šichtovnici za aktuální měsíc.
  const recRange = useMemo(() => {
    const ppl = F.state.clovek?.vals || [];
    const month = (iso) => ({ date_from: monthStart(iso), date_to: monthEnd(iso), user_ids: ppl });
    if (!F.state.obdobi) return month(todayIso());
    const [df, dt] = F.state.obdobi.vals[0].split("..");
    if (df && dt && df <= dt && diffDays(toDate(dt), toDate(df)) <= 61) return { date_from: df, date_to: dt, user_ids: ppl };
    return month(dt || df || todayIso());
  }, [fKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const sheetRange = effView === "sheet" && sheetState ? sheetState : recRange;
  const sheetLabel = sheetRange.date_from === monthStart(sheetRange.date_from) && sheetRange.date_to === monthEnd(sheetRange.date_from)
    ? monthLabel(sheetRange.date_from) : `${czDate(sheetRange.date_from)} – ${czDate(sheetRange.date_to)}`;
  // 403: šichtovnice PDF / Excel / CSV za otevřené období a pro majitele, asistentku a účetní i S částkami
  const role = useAuth()?.user?.role;
  const sheetItems = sheetMenuItems({ label: sheetLabel, canMoney: PAY_ROLES.includes(role), icons: { pdf: FileText, xlsx: Table2, csv: Download, money: Banknote },
    onPick: (fmt, money) => downloadSheet({ range: sheetRange, label: sheetLabel, fmt, money, run: runExport,
      fillHint: narrow ? "Doplníte je v Šichtovnici na počítači." : undefined }) });
  // Zapsat jde komukoli s docházkou — i majiteli, který dělá na stavbě (176b).
  const absPeople = workers.filter((w) => w.role !== "DESIGNER" && (w.status || "active") === "active");

  const s = data?.summary;

  // Záznamy seřazené podle Řadit podle (bez skupin s výchozím řazením drží pořadí serveru) a skupiny podle Seskupit podle.
  const recs = useMemo(() => {
    const all = (data?.days || []).flatMap((d) => d.records);
    if (!needAll) return all;
    const c = ORDER_CMP[gs.k], sign = gs.dir === "desc" ? -1 : 1;
    return [...all].sort((a, b) => c(a, b) * sign || ORDER_CMP.date(b, a));
  }, [data, needAll, gs.k, gs.dir]);
  const groups = useMemo(() => {
    if (gs.g === "none") return null;
    const map = new Map();
    for (const r of recs) {
      const k = recKey(r, gs.g);
      if (!map.has(k)) map.set(k, { key: k, label: gs.g === "worker" ? r.user_name : gs.g === "project" ? r.project_name || "Bez zakázky" : groupLabel(k, gs.g, formatDate), user_id: r.user_id, records: [] });
      map.get(k).records.push(r);
    }
    const out = [...map.values()];
    out.forEach((g) => { g.hours = g.records.reduce((a, r) => a + (r.hours || 0), 0); });
    const people = gs.g === "worker" || gs.g === "project";
    return out.sort(people
      ? (gs.gd ? (a, b) => cs(a.label, b.label) : (a, b) => b.hours - a.hours || cs(a.label, b.label))
      : (a, b) => (gs.gd ? cs(a.key, b.key) : cs(b.key, a.key)));
  }, [recs, gs.g, gs.gd]);
  // skupiny jsou po přepnutí sbalené (176) — lišta nese počet, hodiny a náklad
  const [openG, setOpenG] = useState({});
  useEffect(() => { setOpenG({}); }, [gs.g]);
  // 381: klávesnice jako v Úkolech a Dokladech (jen tabulka na PC) — ↑ ↓ nebo najetí myší vybere záznam, mezerník otevře
  // detail směny; v detailu ↑ ↓ listují dál a mezerník ho zavře. Pořadí = viditelné řádky (u skupin jen rozbalené).
  const navRecs = useMemo(() => (groups ? groups.filter((g) => openG[g.key]).flatMap((g) => g.records) : recs), [groups, openG, recs]);
  const navIdx = useMemo(() => new Map(navRecs.map((r, i) => [r.id, i])), [navRecs]);
  const kb = useListNav("att-rec", navRecs.length, (i) => navRecs[i] && setDetail({ r: navRecs[i] }), { enabled: effView === "records" && !narrow });
  const detailIdx = detail ? navIdx.get(detail.r.id) : undefined;
  const detailNav = detailIdx != null ? { index: detailIdx, count: navRecs.length, onGo: (i) => { kb.setSel(i); setDetail({ r: navRecs[i] }); } } : null;
  const rowNav = narrow ? null : { idx: navIdx, sel: kb.sel, rowProps: kb.rowProps };
  const sortProp = groupSortProp({ groups: ATT_GROUPS, orders: ATT_ORDERS, view: gs, setView: setGs, def: ATT_VIEW,
    onFold: (all) => setOpenG(all ? {} : Object.fromEntries((groups || []).map((g) => [g.key, true]))) });

  const records = effView === "records";
  const views = [
    { value: "records", label: "Záznamy", icon: ListChecks },
    // Šichtovnice jen na šířku; telefon a tablet na výšku mají Můj kalendář (176c).
    ...(narrow ? [{ value: "calendar", label: "Můj kalendář", icon: CalendarDays }] : [{ value: "sheet", label: "Šichtovnice", icon: Table2, count: pendingAbs }]),
  ];

  return (
    <div className="space-y-5" data-testid="attendance-overview-page">
      <ListHeader title="Docházka" mobile={mobile}
        search={qInput} onSearch={records ? setQInput : undefined} searchPh="Hledat jméno, stavbu, report…"
        primary={{ label: "Záznam", full: "Přidat záznam", testid: "add-manual-btn", menu: [
          { label: "Jednotlivý", hint: "jeden člověk, jedna směna", onClick: () => setAdd("single"), testid: "add-single" },
          { label: "Hromadný", hint: "jedna zakázka, více lidí", onClick: () => setAdd("bulk"), testid: "add-bulk" },
          { label: "Nepřítomnost", hint: "dovolená, lékař, nemoc — i více lidem", onClick: () => setAbsOpen(true), testid: "add-absence" },
        ] }}
        moreBusy={!!exporting}
        more={[
          ...sheetItems.filter((x) => !x.sub),
          { icon: FileSpreadsheet, label: "Záznamy Excel", hint: "pro další práci s čísly", onClick: () => doExport("xlsx"), testid: "export-xlsx-btn" },
          { icon: Download, label: "Záznamy CSV", hint: "pro import do účetnictví", onClick: () => doExport("csv"), testid: "export-csv-btn" },
          ...sheetItems.filter((x) => x.sub),
        ]} />
      {topSlot}

      <ListToolbar mobile={mobile} testid="att-toolbar" views={views} view={effView} onView={setView}
        {...(records ? {
          tabs: PERIODS, tab: F.state.obdobi ? "custom" : period, tabLabel: F.state.obdobi && rangeText(F.state.obdobi.vals[0]), onTab: pickPeriod,
          filter: F, search: qInput, onSearch: setQInput, searchPh: "Hledat jméno, stavbu, report…",
          sort: sortProp,
        } : {})} />

      {effView === "sheet" && <AbsenceSheet reloadKey={sheetKey} onState={setSheetState} workers={workers} projects={projects} onRecordSaved={load}
        startDate={F.state.obdobi ? recRange.date_from : undefined} />}
      {effView === "calendar" && <WorkerAbsence selfApproved />}

      {records && (<>
      {/* Statistics — mzdové náklady posílá backend jen majiteli/asistentce */}
      <div className={`grid grid-cols-2 ${s?.total_cost != null ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-3`} data-testid="summary">
        <StatCard icon={Clock} label="Celkem hodin" value={s ? formatHours(s.total_hours) : "—"} testid="sum-hours" />
        {s?.total_cost != null && <StatCard icon={Wallet} label="Mzdové náklady" value={formatCZK(s.total_cost)} brand testid="sum-cost" />}
        <StatCard icon={Users} label="Počet lidí" value={s ? s.people_count : "—"} testid="sum-people" />
        <StatCard icon={ListChecks} label="Počet záznamů" value={s ? s.record_count : "—"} testid="sum-records" />
      </div>

      {loading && !data && <div className="text-muted-foreground text-sm">Načítám…</div>}
      <div className={`transition-opacity motion-reduce:transition-none ${loading && data ? "opacity-60" : ""}`} aria-busy={loading || undefined} data-testid="att-records-area">
        {data && <RecordsView records={recs} groups={groups} by={gs.g} pagination={data.pagination} paged={!needAll} setPage={setPage} rowNav={rowNav}
          open={openG} onToggle={(k) => setOpenG((o) => ({ ...o, [k]: !o[k] }))} onOpen={setDetail} onSheet={(id) => nav(`/dochazka/vykaz/${id}`)} />}
        {!narrow && recs.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pt-2 text-xs text-muted-foreground" data-testid="att-keys">
            <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> vybrat</span>
            <span className="inline-flex items-center gap-1"><Kbd>mezerník</Kbd> otevřít a zavřít detail</span>
          </p>
        )}
      </div>

      </>)}

      <AbsenceDialog open={absOpen} onClose={() => setAbsOpen(false)} mode="manager" people={absPeople}
        onSaved={() => setSheetKey((k) => k + 1)} />
      <AttendanceSingleDialog open={add === "single"} onClose={() => setAdd(null)} workers={workers} projects={projects} onSaved={load} />
      <AttendanceBulkDialog open={add === "bulk"} onClose={() => setAdd(null)} projects={projects} onSaved={load} />
      <ShiftReviewDialog sid={detail?.r?.id || null} mode="detail" canSeeMoney={s?.total_cost != null}
        onClose={() => setDetail(null)} onChanged={load} nav={detailNav} />
    </div>
  );
}

// Tečka stavu = jediný trvalý signál v řádku: běží / čeká na schválení / hotovo.
function StatusDot({ r }) {
  const [cls, title] = r.running ? ["bg-brand animate-pulse motion-reduce:animate-none", "Probíhá"]
    : r.approval_status === "pending" ? ["bg-amber-500", "Čeká na schválení"]
    : ["bg-success", "Uzavřeno"];
  return <span title={title} data-testid={`status-${r.id}`} className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} />;
}

// A nanejvýš jedna červená vlaječka — ta nejzávažnější. Zbytek je v detailu.
function RowFlag({ r }) {
  const why = r.auto_closed ? "Neodhlášen — směnu ukončil systém"
    : r.running_over_12h ? "Běží přes 12 hodin"
    : r.out_of_radius ? `Píchnuto mimo stavbu${r.max_distance_m != null ? ` · ${r.max_distance_m} m` : ""}`
    : null;
  if (!why) return null;
  return (
    <span title={why} data-testid={`row-flag-${r.id}`}
      className="w-[22px] h-[22px] shrink-0 rounded-full bg-destructive/10 text-destructive text-xs font-bold grid place-items-center">!</span>
  );
}

// Tichá stopa po fotkách — samotné náhledy patří do detailu.
function PhotoCount({ r }) {
  const n = r.photos_count ?? (r.photos || []).length;
  if (!n) return null;
  return <span title={plur(n, "fotka", "fotky", "fotek")} className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0"><Camera size={13} />{n}</span>;
}

// Řádky záznamů — tabulka na PC, karty na mobilu. showDate = čistý seznam bez skupin;
// hide = sloupec, který ve skupině jen opakuje její název (379: Pracovník / Stavba).
function RecordRows({ records, onOpen, showDate, hide, rowNav }) {
  const showWorker = hide !== "worker", showSite = hide !== "project";
  const showCost = records.some((x) => x.cost != null);
  // 219: jen jedna varianta v DOM (dřív tabulka i karty, jedna skrytá CSS) — se
  // seskupením až 2 000 záznamů to byl dvojnásobek prvků.
  // 348: karty i na tabletu na výšku (< 1024 px) — tabulka se tam nevešla a uřízla report.
  const mobile = useIsNarrow();
  return (
    <>
      {!mobile && (
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground border-b border-border">
          <tr><th className="p-3 w-8"></th>{showDate && <th className="p-3 font-bold">Datum</th>}{showWorker && <th className="p-3 font-bold">Pracovník</th>}{showSite && <th className="p-3 font-bold">Stavba</th>}<th className="p-3 font-bold">Od–do</th><th className="p-3 font-bold">Hodiny</th>{showCost && <th className="p-3 font-bold">Náklad</th>}<th className="p-3 font-bold w-2/5">Report</th><th className="p-3"></th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {records.map((r) => {
            // 381: řádek nese výběr z klávesnice / myši (rowNav), vybraný má oranžový proužek vlevo
            const ni = rowNav?.idx.get(r.id), on = ni != null && rowNav.sel === ni;
            return (
            <tr key={r.id} {...(ni != null ? rowNav.rowProps(ni) : {})} tabIndex={ni != null ? -1 : undefined} data-testid={`rec-${r.id}`} onClick={() => onOpen({ r })}
              className={`cursor-pointer outline-none transition-colors motion-reduce:transition-none ${on ? "bg-brand/[0.06]" : "hover:bg-secondary/40"}`}>
              <td className={`p-3 ${on ? "shadow-[inset_2px_0_0_hsl(var(--accent-orange))]" : ""}`}><StatusDot r={r} /></td>
              {showDate && <td className="p-3 whitespace-nowrap tabular-nums font-semibold text-muted-foreground">{formatDate(r.date)}</td>}
              {showWorker && <td className="p-3 font-semibold whitespace-nowrap">{r.user_name}</td>}
              {showSite && <td className="p-3 whitespace-nowrap">{r.project_name}</td>}
              <td className="p-3 whitespace-nowrap">
                {formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : <RunningBadge />}
                {r.manual && <span className="ml-2 text-xs text-muted-foreground">ručně</span>}
              </td>
              <td className="p-3 font-semibold">{formatHours(r.hours)}</td>
              {showCost && <td className="p-3 text-brand font-semibold whitespace-nowrap">{formatCZK(r.cost)}</td>}
              <td className="p-3 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 truncate">{r.report || <span className="italic opacity-60">bez reportu</span>}</span>
                  <PhotoCount r={r} /><RowFlag r={r} />
                </div>
              </td>
              <td className="p-3 text-right">
                <Button variant="ghost" size="icon" title="Opravit záznam"
                  onClick={(e) => { e.stopPropagation(); onOpen({ r, edit: true }); }} data-testid={`rec-edit-${r.id}`}><Pencil size={15} /></Button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      )}
      {mobile && (
      <div className="divide-y divide-border">
        {records.map((r) => (
          <button key={r.id} type="button" onClick={() => onOpen({ r })}
            className="w-full p-4 space-y-1 text-left active:bg-secondary/40" data-testid={`rec-card-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-bold flex items-center gap-2 min-w-0"><StatusDot r={r} /><span className="truncate">{showWorker ? r.user_name : formatDate(r.date)}</span></span>
              <span className="font-semibold whitespace-nowrap">{formatHours(r.hours)}{r.cost != null && <> · <span className="text-brand">{formatCZK(r.cost)}</span></>}</span>
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
              {showDate && showWorker && <><span className="tabular-nums font-semibold">{formatDate(r.date)}</span>·</>}
              {showSite && <><span className="truncate">{r.project_name}</span>·</>}
              <span>{formatTime(r.check_in)}–{r.check_out ? formatTime(r.check_out) : "…"}</span>
              {r.manual && <span className="text-xs">ručně</span>}
              <PhotoCount r={r} /><RowFlag r={r} />
            </div>
            <div className="text-sm line-clamp-2">{r.report || <span className="italic text-muted-foreground opacity-60">bez reportu</span>}</div>
          </button>
        ))}
      </div>
      )}
    </>
  );
}

// Záznamy (176): bez skupin = čistý seznam se stránkováním; se skupinami (Den / Týden / Měsíc / Pracovník / Zakázka, 379)
// sbalitelné lišty se součty — u člověka Výkaz →. Sbalit vše / Rozbalit vše je v Zobrazení.
function RecordsView({ records, groups, by, pagination, paged, setPage, open, onToggle, onOpen, onSheet, rowNav }) {
  if (!records.length) return <Empty />;
  return (
    <div className="space-y-3" data-testid="records-view">
      {!groups ? (
        <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="flat-view">
          <RecordRows records={records} onOpen={onOpen} showDate rowNav={rowNav} />
        </div>
      ) : groups.map((g) => {
        const isOpen = !!open[g.key];
        const cost = g.records.reduce((a, r) => a + (r.cost || 0), 0);
        const showCost = g.records.some((x) => x.cost != null);
        const extra = by === "worker" ? plur(new Set(g.records.map((r) => r.date)).size, "den", "dny", "dní")
          : by === "project" ? plur(new Set(g.records.map((r) => r.user_name)).size, "člověk", "lidé", "lidí") : null;
        return (
          <div key={g.key} className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid={`group-${g.key}`}>
            <div className={`flex items-center bg-secondary/50 hover:bg-secondary ${isOpen ? "border-b border-border" : ""}`}>
              <button type="button" onClick={() => onToggle(g.key)} data-testid={`group-toggle-${g.key}`} aria-expanded={isOpen}
                className="flex-1 min-w-0 min-h-[48px] px-4 py-2.5 font-bold flex flex-col items-start justify-center gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 text-left">
                <span className="flex items-center gap-2 min-w-0 max-w-full">
                  <ChevronDown size={15} className={`shrink-0 transition-transform motion-reduce:transition-none ${isOpen ? "" : "-rotate-90"}`} />
                  <span className="truncate">{g.label}</span>
                </span>
                {/* telefon: souhrn pod názvem, ať se jméno nezkracuje */}
                <span className="pl-[23px] sm:pl-0 text-xs text-muted-foreground font-semibold tabular-nums whitespace-nowrap">
                  {extra && <span className="hidden sm:inline">{extra} · </span>}{plur(g.records.length, "záznam", "záznamy", "záznamů")} · {formatHours(g.hours)}<span className="hidden sm:inline">{showCost ? ` · ${formatCZK(cost)}` : ""}</span>
                </span>
              </button>
              {by === "worker" && g.user_id && (
                <Button variant="ghost" size="sm" className="mr-2 shrink-0" onClick={() => onSheet(g.user_id)} data-testid={`worker-sheet-${g.user_id}`}>Výkaz →</Button>
              )}
            </div>
            {isOpen && <RecordRows records={g.records} onOpen={onOpen} showDate={by !== "day"} hide={by} rowNav={rowNav} />}
          </div>
        );
      })}
      {!paged && pagination && pagination.total > records.length && (
        <p className="text-[13px] text-muted-foreground text-center">Zobrazeno prvních {records.length} z {pagination.total} záznamů — zužte období.</p>
      )}
      {paged && pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2" data-testid="pagination">
          <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => setPage(pagination.page - 1)} data-testid="page-prev">Předchozí</Button>
          <span className="text-sm text-muted-foreground">Strana {pagination.page} z {pagination.pages}</span>
          <Button variant="outline" size="sm" disabled={pagination.page >= pagination.pages} onClick={() => setPage(pagination.page + 1)} data-testid="page-next">Další</Button>
        </div>
      )}
    </div>
  );
}

const Empty = () => <div className="bg-card border border-border rounded-[20px] p-10 text-center text-muted-foreground" data-testid="overview-empty">Žádné záznamy odpovídající filtru.</div>;
